// client/src/utils/pricingInvoice.ts
/**
 * The vendor invoice a catalogue row's price came from, by id (D119.2).
 *
 * Owner rule (2026-10-13): the Invoice # on a Master Catalogue row is a link to that vendor invoice,
 * and the link is made from the invoice's ID, never from its printed number (a supplier reuses a
 * number; a different document is a different id). The id lives in `origin.docId`, and only an
 * `origin.docCollection` of 'vendorinvoices' is a vendor invoice: a quote-harvested row names a
 * quotation, and a typed or price-list row names nothing.
 *
 * A price a dated DOCUMENT stated (`meta_data.importedFrom`, the 28 Sep list) leaves `origin` naming
 * the older invoice while the number and date shown are the list's, so that row names no invoice.
 * The sync clears the stamp the next time an invoice reprices the row.
 *
 * Returns the id as a string, or null. Pure. Registered as BUSINESS_RULES#price-invoice-link.
 *
 * MIRRORED FILE: src/utils/pricingInvoice.js is a line-for-line twin of `pricingInvoiceId`. Change one, change both;
 * `mirroredUtilParity.test.js` checks.
 */

export const VENDOR_INVOICE_COLLECTION = 'vendorinvoices';

export function pricingInvoiceId(
  row:
    | {
        origin?: { docId?: unknown; docCollection?: string | null } | null;
        meta_data?: Record<string, any> | null;
        priceSetByHand?: unknown;
      }
    | null
    | undefined
): string | null {
  if (!row || !row.origin || row.origin.docCollection !== VENDOR_INVOICE_COLLECTION) return null;
  if (row.meta_data && row.meta_data.importedFrom) return null;
  // A price a person typed (`priceSetByHand`, which includes the D119.3 keep-piece-price choice) was
  // stated by no invoice: `origin` still names the invoice that priced the row BEFORE the edit, and its
  // Invoice # would open a document that says a different price (lead's ruling, 2026-10-13).
  if (row.priceSetByHand) return null;
  const id = row.origin.docId;
  return id == null || id === '' ? null : String(id);
}
